import { Container, Text, TextStyle } from 'pixi.js';

interface FloatingNumber {
    text: Text;
    startY: number;
    startX: number;
    lifetime: number;
    maxLifetime: number;
    velocityY: number;
    scale: number;
}

export default class FloatingScoreNumbers {
    private container: Container;
    private numbers: FloatingNumber[] = [];
    private readonly DEFAULT_LIFETIME = 1500; // ms
    private readonly DEFAULT_VELOCITY = -50; // pixels per second

    constructor(gameWorld: Container) {
        this.container = new Container();
        gameWorld.addChild(this.container);
    }

    public showNumber(
        value: number | string,
        x: number,
        y: number,
        color: number = 0xFFFFFF,
        size: number = 24,
        lifetime: number = this.DEFAULT_LIFETIME,
        velocityY: number = this.DEFAULT_VELOCITY
    ): void {
        const text = new Text({
            text: typeof value === 'number' ? `+${value}` : value,
            style: new TextStyle({
                fontFamily: 'Arial',
                fontSize: size,
                fill: color,
                stroke: { color: 0x000000, width: 3 },
                fontWeight: 'bold',
                align: 'center'
            })
        });
        text.anchor.set(0.5);
        text.x = x;
        text.y = y;

        this.container.addChild(text);

        const floatingNumber: FloatingNumber = {
            text,
            startY: y,
            startX: x,
            lifetime: 0,
            maxLifetime: lifetime,
            velocityY: velocityY,
            scale: 1.0
        };

        this.numbers.push(floatingNumber);
    }

    public update(delta: number): void {
        const deltaSeconds = delta / 60; // Assuming 60 FPS

        for (let i = this.numbers.length - 1; i >= 0; i--) {
            const num = this.numbers[i];
            num.lifetime += delta * (1000 / 60); // Convert delta to milliseconds

            const progress = num.lifetime / num.maxLifetime;

            // Move upward
            num.text.y = num.startY + (num.velocityY * progress / 1000);

            // Fade out
            num.text.alpha = 1 - progress;

            // Scale up then down
            if (progress < 0.3) {
                num.scale = 1 + progress * 2; // Scale up to 1.6x
            } else {
                num.scale = 1.6 - (progress - 0.3) * 0.6 / 0.7; // Scale down
            }
            num.text.scale.set(num.scale);

            // Slight horizontal drift
            num.text.x = num.startX + Math.sin(progress * Math.PI * 2) * 20;

            // Remove when expired
            if (num.lifetime >= num.maxLifetime) {
                this.container.removeChild(num.text);
                num.text.destroy();
                this.numbers.splice(i, 1);
            }
        }
    }

    public clear(): void {
        this.numbers.forEach(num => {
            this.container.removeChild(num.text);
            num.text.destroy();
        });
        this.numbers = [];
    }
}

